/**
 * Brand badge lookups.
 *
 * Kept separate from `#/lib/brand/runtime` (validation/merge) and free of React
 * so it can be called from render paths and unit-tested directly.
 *
 * Everything here reads the ALREADY-VALIDATED `BRAND.badges` — the document was
 * sanitized once at module init, so these are plain map reads with no
 * per-render parsing or network work. Lists call `badgesForDid` once per row,
 * so it must stay O(badges-held).
 */

import {BRAND} from '#/lib/brand'
import {type BrandBadgeDef} from '#/lib/brand/types'

const EMPTY: BrandBadgeDef[] = []

/** True when this deployment has any badges at all (most brands: false). */
export function brandHasBadges(): boolean {
  return !!BRAND.badges && BRAND.badges.defs.length > 0
}

/**
 * The badge definitions `did` holds, in the operator's definition order so
 * every profile shows them consistently (the holder map's own order is
 * whatever the server serialized).
 *
 * Returns a shared empty array when there is nothing to show, so callers can
 * bail on `.length` without allocating.
 */
export function badgesForDid(did: string | undefined): BrandBadgeDef[] {
  const badges = BRAND.badges
  if (!badges || !did) return EMPTY
  const slugs = badges.holders[did]
  if (!slugs || slugs.length === 0) return EMPTY
  const held = new Set(slugs)
  const out = badges.defs.filter(def => held.has(def.slug))
  return out.length > 0 ? out : EMPTY
}

/** Every defined badge, in operator order. */
export function allBrandBadges(): BrandBadgeDef[] {
  return BRAND.badges?.defs ?? EMPTY
}

/**
 * Badges `did` does NOT hold that can be bought — the "get a badge" set.
 */
export function purchasableBadgesForDid(
  did: string | undefined,
): BrandBadgeDef[] {
  const badges = BRAND.badges
  if (!badges) return EMPTY
  const held = new Set(did ? (badges.holders[did] ?? []) : [])
  const out = badges.defs.filter(def => def.purchaseUrl && !held.has(def.slug))
  return out.length > 0 ? out : EMPTY
}

/**
 * The checkout URL for `def`, carrying the buyer's DID so pds-pro's
 * `/hooks/stripe` can attribute the purchase.
 *
 * The DID is base64url-encoded because Stripe's `client_reference_id` accepts a
 * limited character set and a raw `did:plc:…` contains `:`. pds-pro decodes it
 * on the webhook side; the encoding must stay in lockstep with that.
 *
 * Returns undefined when the badge is not purchasable or the DID is missing —
 * callers must not fabricate a checkout that cannot be credited to anyone.
 */
export function badgePurchaseUrl(
  def: BrandBadgeDef,
  did: string | undefined,
): string | undefined {
  if (!def.purchaseUrl || !did) return undefined
  try {
    const url = new URL(def.purchaseUrl)
    url.searchParams.set('client_reference_id', base64UrlEncode(did))
    return url.toString()
  } catch {
    return undefined
  }
}

/**
 * base64url (RFC 4648 §5): standard base64 with `+/` → `-_` and no padding.
 *
 * Hand-rolled rather than pulled from a polyfill because this runs on web and
 * on Hermes, where `btoa` is not guaranteed. Input is a DID — ASCII by spec —
 * but the UTF-8 step keeps it correct for anything else.
 */
function base64UrlEncode(input: string): string {
  const bytes = utf8Bytes(input)
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i]
    const b1 = bytes[i + 1]
    const b2 = bytes[i + 2]
    out += alphabet[b0 >> 2]
    out += alphabet[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)]
    if (b1 === undefined) break
    out += alphabet[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)]
    if (b2 === undefined) break
    out += alphabet[b2 & 0x3f]
  }
  return out
}

function utf8Bytes(input: string): number[] {
  const bytes: number[] = []
  for (const char of input) {
    let code = char.codePointAt(0)!
    if (code < 0x80) {
      bytes.push(code)
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f))
    } else if (code < 0x10000) {
      bytes.push(
        0xe0 | (code >> 12),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      )
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      )
    }
  }
  return bytes
}
